import type { JobStatusDto, ModelReportRedisDto, ModelSoftwareInfoDto } from "../../../api";
import { getModelStatusKindByState } from "./table-view.lib";

export type ConvertJobStatusType = "error" | "in_progress";

export type ConvertJobStatus = {
  type: ConvertJobStatusType;
  textError?: string;
  date?: string;
};

export const CONVERT_JOB_TITLES: Record<string, string> = {
  GEOMETRY: "Геометрия",
  PROPERTIES: "Свойства",
};

export const CONVERT_JOB_STATUS_LABELS: Record<ConvertJobStatusType, string> = {
  error: "Ошибка конвертации",
  in_progress: "В процессе",
};

const CONVERT_ERROR_TRANSLATIONS: { text: string; textRu: string }[] = [
  { text: "uploadResult failed", textRu: "Не удалось записать результаты" },
  { text: "The maximum number of retries has been exceeded", textRu: "Превышено количество попыток обработки" },
  {
    text: "Reconversion cleanup or scheduling failed. Retry reconversion",
    textRu: "Сбой при планировании операции переконвертации. Повторите попытку переконвертации",
  },
];

const SUBPROCESS_ERROR_RE = /subprocess\s+(?:exit|error)\s+code(?:\s+is)?\s*(\d+)?/i;

export const MODEL_STATUS_QUERY_KEY = (id: string) => ["filesystem", "model-status", id] as const;
export const MODEL_SOFTWARE_QUERY_KEY = (id: string) => ["filesystem", "model-software", id] as const;
export const MODEL_REPORT_QUERY_KEY = (id: string) => ["filesystem", "model-report", id] as const;

export const translateConvertError = (text: string): string => {
  const subprocessMatch = text.match(SUBPROCESS_ERROR_RE);

  if (subprocessMatch) {
    return subprocessMatch[1] ? `Код ошибки внутреннего процесса ${subprocessMatch[1]}` : "Код ошибки внутреннего процесса";
  }

  const normalized = text.trim().toLowerCase();
  return CONVERT_ERROR_TRANSLATIONS.find((entry) => normalized.includes(entry.text.toLowerCase()))?.textRu ?? text;
};

export const parseConvertStatusMessage = (message?: string | null): Pick<ConvertJobStatus, "textError" | "date"> => {
  if (!message) {
    return {};
  }

  const match = message.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  const text = (match?.[1] ?? message).trim();
  const date = match?.[2]?.trim();

  return { textError: text ? translateConvertError(text) : undefined, date };
};

export const buildConvertJobStatuses = (jobs: JobStatusDto[]): Record<string, ConvertJobStatus> => {
  return jobs.reduce<Record<string, ConvertJobStatus>>((acc, job) => {
    const outputFormat = job.outputFormat;
    const state = job.state?.toLowerCase();

    if (!outputFormat || !CONVERT_JOB_TITLES[outputFormat]) {
      return acc;
    }

    if (state === "failed" || state === "error") {
      acc[outputFormat] = { type: "error", ...parseConvertStatusMessage(job.statusMessage) };
    } else if (state === "waiting" || state === "running" || state === "working") {
      acc[outputFormat] = { type: "in_progress", ...parseConvertStatusMessage(job.statusMessage) };
    }

    return acc;
  }, {});
};

export const mergeConvertJobStatuses = (
  jobs: JobStatusDto[],
  overrides: Record<string, ConvertJobStatus | null>,
): Record<string, ConvertJobStatus> => {
  const next = buildConvertJobStatuses(jobs);

  for (const [jobKey, status] of Object.entries(overrides)) {
    if (status) {
      next[jobKey] = status;
    } else {
      delete next[jobKey];
    }
  }

  return next;
};

export const getConvertJobStatusByState = (state: string, statusMessage?: string | null): ConvertJobStatus | null => {
  const kind = getModelStatusKindByState(state);

  if (kind === "error") {
    return { type: "error", ...parseConvertStatusMessage(statusMessage) };
  }

  if (kind === "pending") {
    return { type: "in_progress" };
  }

  return null;
};

export const getConvertJobKeys = (outputFormat?: string | null): string[] =>
  outputFormat && CONVERT_JOB_TITLES[outputFormat] ? [outputFormat] : Object.keys(CONVERT_JOB_TITLES);

export const buildInProgressConvertJobStatuses = (): Record<string, ConvertJobStatus> =>
  Object.fromEntries(Object.keys(CONVERT_JOB_TITLES).map((jobKey) => [jobKey, { type: "in_progress" as const }]));

type ConvertReportTextParams = {
  title: string;
  report?: ModelReportRedisDto;
  model?: ModelSoftwareInfoDto;
};

export const buildConvertReportText = ({ title, report, model }: ConvertReportTextParams): string => {
  let reportContent = `ОТЧЁТ О КОНВЕРТАЦИИ ФАЙЛА: ${title}\n\n`;

  reportContent += `Незамкнутые элементы поверхности\n`;
  reportContent += `${report?.unclosed?.join(", ")}\n\n`;

  reportContent += `Удаленные точки модели\n`;
  reportContent += `${report?.deleted?.join(", ")}\n\n`;

  reportContent += `Найденные прокси элементы\n`;
  reportContent += `${report?.proxy?.join(", ")}\n\n`;

  reportContent += `Элементы без геометрии\n`;
  reportContent += `${report?.withoutGeometry?.join(", ")}\n\n`;

  reportContent += `Нормали\n`;
  reportContent += `${report?.withoutNormals?.join(", ")}\n\n`;

  reportContent += `Пустые поля\n`;
  reportContent += `${report?.emptyFieldsCount}\n\n`;

  reportContent += `Версия ПО\n`;
  reportContent += `${model?.softwareName} ${model?.softwareVersion}\n`;

  return reportContent;
};
